<template>
  <div>
    <b-breadcrumb :items="breadcrumbItems"></b-breadcrumb>
    <div class="row">
      <div class="col-sm-6">
        <h4 qid="access-controls-page-title" class="float-left">
          {{ $t('maintenance.access_control.title') }}
        </h4>
        <div class="mt-2 ml-3 float-left">
          <span v-if="getGuide('access_control_list')">
            <GuideButton :guide="getGuide('access_control_list')"></GuideButton>
          </span>
        </div>
      </div>
      <div class="col-sm-6 text-right">
        <router-link
            to="/maintenance/access-control/create"
            class="btn btn-success float-right"
            qid="create-ac-button"
            v-if="isGranted(permissions.ACCESS_CONTROL_CREATE)"
        >
          <font-awesome-icon icon="plus-circle"/> {{ $t('maintenance.access_control.create_button') }}
        </router-link>
        <b-form-input
          type="text"
          v-model="filters.keywordValue"
          class="float-right mw-300 mr-3 search-bar"
          qid="search-ac-input"
          v-on:input="debounceInput"
          :placeholder="$t('maintenance.access_control.search_placeholder')"
        ></b-form-input>
      </div>
    </div>

    <div class="row">
      <div class="col-sm-10 table-container">
        <div class="card">
          <table class="table table-responsive-md">
            <thead>
            <tr>
              <th scope="col"
                  class="cursor-pointer"
                  @click="selectSort('name')"
                  qid="sort-ac-name"
              >
                {{ $t('maintenance.access_control.table.name') }}
                <span class="ml-2">
                  <font-awesome-icon class="text-muted" icon="sort" v-if="selectedSort !== 'name'"/>
                  <font-awesome-icon icon="caret-down" v-if="selectedDirection === 'desc' && selectedSort === 'name'"/>
                  <font-awesome-icon icon="caret-up" v-if="selectedDirection === 'asc' && selectedSort === 'name'"/>
                </span>
              </th>
              <th>{{ $t('maintenance.access_control.table.provisioning') }}</th>
              <th>{{ $t('maintenance.access_control.table.certification') }}</th>
              <th width="130"></th>
            </tr>
            </thead>
            <tbody>
              <tr v-for="object in tableItems">
                <td>
                  <router-link
                      :to="{ name: 'maintenance.access_control.view', params: { slug: object.slug }}"
                      qid="ac-view-page-link"
                  >
                    {{object.name}}
                  </router-link>
                </td>
                <td>
                  <span v-if="object.provisioning == 1" qid="ac-provisioning-yes">
                    {{ $t('system.yes') }}
                  </span>
                  <span v-if="object.provisioning == 0" qid="ac-provisioning-no">
                    {{ $t('system.no') }}
                  </span>
                </td>
                <td>
                  <span v-if="object.re_certification == 1" qid="ac-recertification-yes">
                    {{ $t('system.yes') }}
                  </span>
                  <span v-if="object.re_certification == 0" qid="ac-recertification-no">
                    {{ $t('system.no') }}
                  </span>
                </td>
                <td>
                  <span v-if="isGranted([permissions.ACCESS_CONTROL_EDIT, permissions.ACCESS_CONTROL_EDIT_OWN], object.created_by) && (!getClient.parent || (getClient.parent && object.partition))"
                        class="btn btn-edit"
                        qid="edit-ac-modal"
                        @click="showEditACModal(object)"
                  >
                    <font-awesome-icon icon="pen"/>
                  </span>
                  <span v-if="isGranted([permissions.ACCESS_CONTROL_DELETE, permissions.ACCESS_CONTROL_DELETE_OWN], object.created_by) && (!getClient.parent || (getClient.parent && object.partition))"
                        class="btn btn-delete"
                        qid="delete-ac-modal"
                        @click="showConfirmDeleteModal(object)"
                  >
                    <font-awesome-icon icon="trash-alt"/>
                  </span>
                </td>
              </tr>
              <tr v-if="!loading && !tableItems.length" qid="no-results-row" class="text-center">
                <td colspan="10">
                  <h5 class="mt-3">
                    {{ $t('system.no_results') }}
                  </h5>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="mt-3 pagination row">
          <div class="col-md-6">
            <div class="row">
              <div class="float-left">
                <span class="text-muted pagination-counter">
                  {{ $t('pagination.entries_per_page') }}:
                </span>
              </div>
              <div class="float-left">
                <multiselect
                    v-model="perPage"
                    :options="perPageOptions"
                    track-by="value"
                    label="value"
                    qid="create-org-form-organization-level"
                    :select-label="$t('system.dropdown_select_label')"
                    :selected-label="$t('system.dropdown_selected_label')"
                    :deselect-label="$t('system.dropdown_deselect_label')"
                    @select="perPageChanged"
                    :placeholder="selectedPerPage"
                    @remove="perPageRemoved"
                ></multiselect>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <b-pagination
                class="float-right"
                v-model="currentPage"
                :total-rows="rows"
                :per-page="selectedPerPage"
                aria-controls="my-table"
                @change="pageChanged"
                limit="1"
            >
              <span class="text-muted" slot="first-text">
                <font-awesome-icon icon="angle-double-left"/>
              </span>
              <span class="text-muted" slot="prev-text">
                <font-awesome-icon icon="angle-left"/>
              </span>
              <span class="text-muted" slot="next-text">
                <font-awesome-icon icon="angle-right"/>
              </span>
              <span class="text-muted" slot="last-text">
                <font-awesome-icon icon="angle-double-right"/>
              </span>
              <div slot="ellipsis-text">
                <b-spinner small type="grow"></b-spinner>
                <b-spinner small type="grow"></b-spinner>
                <b-spinner small type="grow"></b-spinner>
              </div>
              <span class="text-muted" slot="page" slot-scope="{ page, active }">
                <span v-if="active">{{getPerPage}}</span>
              </span>
            </b-pagination>
          </div>
        </div>
      </div>
    </div>


    <!-- Delete access control confirm modal template -->
    <b-modal ref="delete-access-control" hide-footer>
      <div class="d-block text-center">
        <h4 qid="delete-ac-title">
          {{ $t('maintenance.access_control.delete.title') }}
        </h4>
      </div>
      <h5 class="text-center">
        {{ $t('maintenance.access_control.delete.description') }} {{ itemToDelete.name }}
      </h5>
      <h5 class="text-center">
        {{ $t('maintenance.access_control.delete.are_you_sure') }}
      </h5>
      <div class="text-center">
        <b-button class="mt-3"
                  variant="secondary"
                  @click="hideConfirmDeleteModal"
                  qid="delete-ac-cancel-button"
        >
          {{ $t('maintenance.access_control.delete.cancel') }}
        </b-button>
        <b-button @click="confirmDeleteAC(itemToDelete)"
                  class="mt-3 ml-3"
                  variant="success"
                  qid="delete-ac-submit-button"
                  v-if="isGranted([permissions.ACCESS_CONTROL_DELETE, permissions.ACCESS_CONTROL_DELETE_OWN], itemToDelete.created_by) && (!getClient.parent || (getClient.parent && itemToDelete.partition))"
        >
          {{ $t('maintenance.access_control.delete.submit') }}
        </b-button>
      </div>
    </b-modal>


    <!-- Edit access control modal template -->
    <b-modal ref="edit-access-control" hide-footer>
      <div class="d-block text-center">
        <h4 qid="update-ac-title">
          {{ $t('maintenance.access_control.update.title') }}
        </h4>
      </div>
      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="edit-ac-error"
      >
        {{ error }}
      </b-alert>
      <b-form @submit="editACFormSubmit" qid="edit-ac-form">
        <div class="form-container">
          <b-form-group>
            <label qid="edit-ac-name-label">
              {{ $t('maintenance.access_control.create.name_label') }}
            </label>
            <b-form-input
              qid="edit-ac-form-name"
              type="text"
              v-model="acForm.name"
              :maxLength="255"
              required
              :placeholder="$t('maintenance.access_control.create.name_placeholder')"
            ></b-form-input>
          </b-form-group>
          <b-form-group>
            <label qid="edit-ac-provisioning-label">
              {{ $t('maintenance.access_control.create.provisioning_label') }}
            </label>
            <b-form-radio-group
              v-model="acForm.provisioning"
              :options="radioOptions"
              qid="edit-ac-form-provisioning"
            ></b-form-radio-group>
          </b-form-group>
          <b-form-group>
            <label qid="edit-ac-certification-label">
              {{ $t('maintenance.access_control.create.certification_label') }}
            </label>
            <b-form-radio-group
              v-model="acForm.re_certification"
              :options="radioOptions"
              qid="edit-ac-form-certification"
            ></b-form-radio-group>
          </b-form-group>
        </div>
        <div class="text-right">
          <b-button class="mt-3"
                    variant="secondary"
                    @click="hideEditACModal"
                    qid="edit-ac-cancel-button"
          >
            {{ $t('maintenance.access_control.create.cancel') }}
          </b-button>
          <b-button type="submit"
                    class="mt-3 ml-3"
                    variant="success"
                    :disabled="buttonDisabled"
                    qid="edit-ac-submit-button"
                    v-if="isGranted([permissions.ACCESS_CONTROL_EDIT, permissions.ACCESS_CONTROL_EDIT_OWN], itemObject.created_by) && (!getClient.parent || (getClient.parent && itemObject.partition))"
          >
            <span v-if="buttonDisabled" class="mr-1">
              <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
            </span>
            {{ $t('maintenance.access_control.create.submit') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>

  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';

  export default {
    name: 'AccessControl',
    data: function() {
      return {
        breadcrumbItems: [
          {
            text: this.$t('dashboard.title'),
            to: '/'
          },
          {
            text: this.$t('maintenance.title'),
            to: '/maintenance'
          },
          {
            text: this.$t('maintenance.access_control.title'),
            active: true
          }
        ],
        tableItems: [],

        // Pagination
        perPageOptions: [
          { name: '10', value: '10' },
          { name: '20', value: '20' },
          { name: '50', value: '50' },
          { name: '100', value: '100' }
        ],
        perPage: '',
        selectedPerPage: "10",
        currentPage: 1,
        totalRows: 0,

        itemToDelete: {},
        itemObject: {},
        acForm: {
          name: '',
          provisioning: '',
          re_certification: '',
          client:'',
        },
        error: '',
        selectedSort: '',
        selectedDirection: '',
        filters: {
          keywordValue: ''
        },
        loading: true,
        radioOptions: [
          {text: this.$t('system.yes'), value: 1},
          {text: this.$t('system.no'), value: 0}
        ],
        buttonDisabled: false,
        myGuides: [],
      }
    },
    computed: {
      rows() {
        // Use computed value for rows because of dynamic update
        return this.totalRows
      },
      getFilters() {
        return this.$store.state.activeFilters
      },
      getStorePerPage() {
        return this.$store.state.perPage
      },
      getClient() {
        return this.$store.state.client
      },
      getPerPage() {
        let page = this.currentPage;
        let selectedFrom = (page-1) * Number(this.selectedPerPage) + 1;
        let selectedTo = (page-1) * Number(this.selectedPerPage) + Number(this.selectedPerPage);
        let selectedOf = this.totalRows;

        if(selectedTo > selectedOf){
          selectedTo = selectedOf
        }

        return selectedFrom + " - " + selectedTo + " of " + selectedOf
      }
    },
    watch: {
      // Watch when client changes in store and trigger api reload
      getClient: function () {
        this.loadContent();
      }
    },
    methods: {
      debounceInput: _.debounce(function() {
        this.loadContent()
      }, 400),
      selectSort(sort) {
        // If old and new sort are same, switch just direction
        if (sort === this.selectedSort) {
          this.selectedDirection = this.selectedDirection === 'desc' ? 'asc' : 'desc';
        } else {
          // If different sort, set sort and set direction to default
          this.selectedDirection = 'desc'
          this.selectedSort = sort;
        }
        this.loadContent();
      },
      getGuide(code) {
        let existingGuide = _.find(this.myGuides, function(object) {
          return object.scope == code && object.type == 'Maintenance'
        });
        return existingGuide
      },
      loadGuides(code) {
        let self = this;
        let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';

        piincHttp.get('guides', { params:
          {
            per_page: 1,
            language: 'en',
            type: 'Maintenance',
            scope: code,
            client: this.getClient.slug
          }
        }).then(function(response) {
          if (response.data.items.length) {
            self.myGuides.push(response.data.items[0])
          }
        }, function() {});
      },
      openHelpCenter(object) {
        let topElement = document.getElementById("app");
        if (topElement.classList.contains("guide-collapsed")) {
          topElement.classList.toggle("guide-collapsed");
        }
        if (!topElement.classList.contains("activity-collapsed")) {
          topElement.classList.toggle("activity-collapsed");
        }

        this.$store.commit('storeHelpCenterState', true);
        this.$store.commit('storeActiveGuideScope', object);
      },
      loadContent(page, perPage) {

        if (this.getStorePerPage) {
          this.selectedPerPage = this.getStorePerPage;
        }

        // If page is not defined, set page to default 1
        let pageNumber = page ? page : 1;
        let perPageNumber = perPage ? perPage : this.selectedPerPage;
        let self = this;

        piincHttp.get('access-controls', { params:
          {
            page: pageNumber,
            per_page: perPageNumber,
            sort: this.selectedSort !== '' ? this.selectedSort : undefined,
            order: this.selectedDirection !== '' ? this.selectedDirection : undefined,
            keyword: this.filters.keywordValue !== '' ? this.filters.keywordValue : undefined,
            client: this.getClient.slug
          }
        }).then(function(response) {
          self.loading = false;
          self.tableItems = response.data.items;
          // Set number of total items
          self.totalRows = response.data.total_count;
        }, function() {});
      },
      pageChanged(page) {
        this.loadContent(page)
      },
      perPageChanged(perPage) {
        let self = this;
        // Load content when page changed listener is triggered
        this.loadContent(1, perPage.value)
        self.selectedPerPage = perPage.value
        this.$store.commit('storePerPage', this.selectedPerPage);
      },
      perPageRemoved() {
        let self = this;
        // Load content when page changed listener is triggered
        self.selectedPerPage = self.perPageOptions[0].value
        this.loadContent(1, self.selectedPerPage)
      },
      showConfirmDeleteModal(object) {
        this.itemToDelete = object
        this.$refs['delete-access-control'].show()
      },
      hideConfirmDeleteModal() {
        this.itemToDelete = {}
        this.$refs['delete-access-control'].hide()
      },
      showEditACModal(object) {

        let self = this;
        this.itemObject = object;
        this.acForm.slug = object.slug;
        this.acForm.name = object.name;
        this.acForm.re_certification = object.re_certification;
        this.acForm.provisioning = object.provisioning;
        this.$refs['edit-access-control'].show()
      },
      hideEditACModal() {
        this.$refs['edit-access-control'].hide()
      },
      editACFormSubmit(evt) {
        this.buttonDisabled = true;
        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        evt.preventDefault();
        this.acForm.client = this.getClient.slug;

        piincHttp.put('access-controls/' + this.acForm.slug, this.acForm).then(function() {
          self.hideEditACModal();
          self.loadContent(self.currentPage);
          self.$toastr('success', self.$t('maintenance.access_control.update.success'))
          self.buttonDisabled = false;
        }, function(error) {
          self.buttonDisabled = false
          if (error.status !== 200) {
            self.error = error.data.message;
          }
        })
      },
      confirmDeleteAC(object) {
        let self = this;
        piincHttp.delete('access-controls/' + object.slug).then(function(response) {
          self.hideConfirmDeleteModal();
          self.loadContent(self.currentPage);
          // Trigger toastr message after modal close
          self.$toastr('success', self.$t('maintenance.access_control.delete.success'))
        }, function() {});
      }
    },
    components: {

    },
    beforeRouteLeave(to, from, next) {
      let object = {
        type: 'access_control',
        filters: this.filters
      }
      this.$store.commit('storeActiveFilters', object);
      this.$store.commit('storePerPage', this.selectedPerPage);
      next()
    },
    created () {
      if (this.getClient && this.getClient.slug) {
        if (this.getFilters.access_control) {
          this.filters = this.getFilters.access_control
        }
        this.loadContent();
      }
      this.loadGuides('access_control_list');
    }
  }
</script>
